import type { components } from '@connected/api-client/api.generated';

import { Checkbox } from '@connected/ui/components/checkbox';
import { Label } from '@connected/ui/components/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@connected/ui/components/tooltip';
import { cn } from '@connected/ui/lib/utils';
import { useTranslation } from 'react-i18next';

type ElectricalInspectionKind = components['schemas']['ElectricalInspectionKind'];

export type RestrictedOption = {
    value: string;
    label: string | string[];
    disabled: boolean;
    inspectionKind: ElectricalInspectionKind;
};

type RestrictedMultipleOptionItemProps = {
    option: RestrictedOption;
    selected: boolean;
    onSelect: () => void;
    name?: string;
};

/**
 * A custom MultipleOptionItem that supports per-option disabled state
 * and shows a tooltip explaining why an option is disabled.
 */
export function RestrictedMultipleOptionItem({
    option,
    selected,
    onSelect,
    name,
}: RestrictedMultipleOptionItemProps) {
    const { t } = useTranslation('applications');

    const content = (
        <Label
            htmlFor={`${name}-${option.value}`}
            className={cn(
                'flex h-fit min-h-10 cursor-pointer items-center gap-2 rounded-md px-2 py-2',
                option.disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            <Checkbox
                id={`${name}-${option.value}`}
                checked={selected}
                onCheckedChange={() => {
                    if (!option.disabled) {
                        onSelect();
                    }
                }}
                disabled={option.disabled}
                name={name}
            />
            <span className="text-sm">{option.label}</span>
        </Label>
    );

    if (option.disabled) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div>{content}</div>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{t('request-concerning.form.instructions.combination-not-allowed')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return content;
}
